import React, { useState, useEffect } from 'react';
import { usePushNotificationContext } from '../../contexts/PushNotificationContext';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';

const PushNotificationBanner = () => {
  const {
    isSupported,
    permission,
    isSubscribed,
    isAuthenticated,
    enablePushNotifications
  } = usePushNotificationContext();

  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Show banner only when:
  // 1. User is authenticated (never prompt guests)
  // 2. Push notifications are supported by the browser
  // 3. Permission has not been granted yet
  // 4. User is not already subscribed
  // 5. User hasn't dismissed the banner before
  useEffect(() => {
    const bannerDismissed = localStorage.getItem('pushNotificationBannerDismissed');

    if (
      isAuthenticated &&
      isSupported &&
      permission === 'default' &&
      !isSubscribed &&
      !bannerDismissed
    ) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isAuthenticated, isSupported, permission, isSubscribed]);

  const handleEnable = async () => {
    setIsLoading(true);
    try {
      const success = await enablePushNotifications();
      if (success) {
        setIsVisible(false);
      } else {
        // Show specific error if permission was denied
        if (permission === 'denied') {
          toast.error('Push notifications were blocked. Please enable them in your browser settings.');
        }
      }
    } catch (error) {
      console.error('Failed to enable push notifications:', error);
      // Show user-friendly error message
      if (error.message === 'Notification permission denied') {
        toast.error('Notification permission was denied. You can enable it later in your browser settings.');
      } else {
        toast.error('Failed to enable push notifications. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pushNotificationBannerDismissed', 'true');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 right-4 left-4 sm:left-auto max-w-md z-50 animate-slide-up">
      <div className="bg-white dark:bg-admin-slate-800 border border-admin-slate-200 dark:border-admin-slate-700 shadow-lg rounded-lg p-4 sm:p-6">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-admin-ucla-100 dark:bg-admin-ucla-900/30 flex items-center justify-center">
              <svg className="w-5 h-5 text-admin-ucla-600 dark:text-admin-ucla-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5z" />
              </svg>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-admin-slate-900 dark:text-admin-slate-100">
              Enable Push Notifications
            </h3>
            <p className="mt-1 text-sm text-admin-slate-500 dark:text-admin-slate-400">
              Get notified about new products, sales, and important updates!
            </p>

            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button
                onClick={handleEnable}
                disabled={isLoading}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-admin-ucla-600 hover:bg-admin-ucla-700 dark:bg-admin-ucla-700 dark:hover:bg-admin-ucla-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-admin-ucla-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enabling...
                  </>
                ) : (
                  'Enable Notifications'
                )}
              </button>
              <button
                onClick={handleDismiss}
                className="inline-flex items-center justify-center px-4 py-2 border border-admin-slate-300 dark:border-admin-slate-600 text-sm font-medium rounded-md text-admin-slate-700 dark:text-admin-slate-300 bg-white dark:bg-admin-slate-700 hover:bg-admin-slate-50 dark:hover:bg-admin-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-admin-ucla-500 transition-colors duration-200"
              >
                Maybe Later
              </button>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="flex-shrink-0 ml-2 text-admin-slate-400 hover:text-admin-slate-500 dark:hover:text-admin-slate-300 transition-colors duration-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationBanner;
